import { initialCart } from "../ObjectsInitialValues";
import { CART_KEY as LEGACY_CART_KEY } from "./StorageKeys";

const DB_NAME = "dm2buyDB";
const DB_VERSION = 1;
const STORE_NAME = "cartStore";
const CART_KEY = "cart";

// Checks if localStorage exists and is accessible/writable
function isLocalStorageAvailable() {
  try {
    // Check if window and localStorage are defined
    if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
      // console.warn("LocalStorage is not defined.");
      return false;
    }
    // Try to use localStorage to catch security errors
    const testKey = "__cart_storage_test__";
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    // SecurityError or other exceptions mean it's not available/accessible
    console.warn(`LocalStorage is not available or accessible: ${e.message}`);
    return false;
  }
}

// --- IndexedDB Helpers ---

function openDB() {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !window.indexedDB) {
      console.warn("IndexedDB not supported.");
      return reject("IndexedDB not supported");
    }
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error("IndexedDB error:", event.target.error);
      reject("IndexedDB error: " + event.target.error);
    };

    request.onsuccess = (event) => {
      resolve(event.target.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
        console.log("IndexedDB object store created.");
      }
    };
  });
}

async function saveToIndexedDB(cartData) {
  let db;
  try {
    db = await openDB();
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    // Use a fixed key for the cart data
    const dataToStore = { id: CART_KEY, ...cartData };
    const request = store.put(dataToStore);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        console.log("Cart saved to IndexedDB");
        resolve(true);
      };
      request.onerror = (event) => {
        console.error("Error saving cart to IndexedDB:", event.target.error);
        reject(event.target.error);
      };
      transaction.oncomplete = () => {
        db.close();
      };
      transaction.onerror = (event) => {
        console.error("IndexedDB transaction error:", event.target.error);
        db.close();
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error("Failed to open DB for saving:", error);
    if (db) db.close();
    return Promise.reject(error);
  }
}

async function getFromIndexedDB() {
  let db;
  try {
    db = await openDB();
    const transaction = db.transaction([STORE_NAME], "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(CART_KEY); // Use the fixed key

    return new Promise((resolve, reject) => {
      request.onsuccess = (event) => {
        const { result } = event.target;
        if (result) {
          // console.log("Cart retrieved from IndexedDB");
          const { id, ...cartData } = result; // Exclude the 'id' key
          resolve(cartData);
        } else {
          // console.log("No cart data found in IndexedDB");
          resolve(null);
        }
      };
      request.onerror = (event) => {
        console.error("Error retrieving cart from IndexedDB:", event.target.error);
        reject(event.target.error);
      };
      transaction.oncomplete = () => {
        db.close();
      };
      transaction.onerror = (event) => {
        console.error("IndexedDB transaction error:", event.target.error);
        db.close();
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error("Failed to open DB for retrieval:", error);
    if (db) db.close();
    return Promise.resolve(null); // Resolve with null if DB opening fails
  }
}

async function deleteFromIndexedDB() {
  let db;
  try {
    db = await openDB();
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(CART_KEY); // Use the fixed key

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        // console.log("Cart deleted from IndexedDB");
        resolve(true);
      };
      request.onerror = (event) => {
        console.error("Error deleting cart from IndexedDB:", event.target.error);
        reject(event.target.error);
      };
      transaction.oncomplete = () => {
        db.close();
      };
      transaction.onerror = (event) => {
        console.error("IndexedDB transaction error:", event.target.error);
        db.close();
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error("Failed to open DB for deletion:", error);
    if (db) db.close();
    return Promise.reject(error);
  }
}

// --- LocalStorage Helpers ---

function saveToLocalStorage(cartData) {
  // Use the availability check function
  if (!isLocalStorageAvailable()) {
    // console.warn("Attempted to save to LocalStorage, but it's unavailable.");
    return false;
  }
  try {
    const cartString = JSON.stringify(cartData);
    window.localStorage.setItem(CART_KEY, cartString);
    // console.log("Cart saved to LocalStorage");
    return true;
  } catch (error) {
    // Catch errors during the stringify/setItem process itself
    console.error("Error during saveToLocalStorage operation:", error);
    if (error.name === "QuotaExceededError") {
      console.warn("LocalStorage quota exceeded. Clearing cart data from LocalStorage.");
      deleteFromLocalStorage(); // This is safe because it also checks availability
    }
    return false;
  }
}

function getFromLocalStorage() {
  // Use the availability check function
  if (!isLocalStorageAvailable()) {
    // console.warn("Attempted to get from LocalStorage, but it's unavailable.");
    return null;
  }
  try {
    const cartString = window.localStorage.getItem(CART_KEY);
    if (cartString) {
      // console.log("Cart retrieved from LocalStorage");
      return JSON.parse(cartString);
    }
    // console.log("No cart data found in LocalStorage");
    return null;
  } catch (error) {
    // Catch errors during the getItem/JSON.parse process itself
    console.error("Error during getFromLocalStorage operation:", error);
    deleteFromLocalStorage(); // Attempt to clear potentially corrupted item
    return null;
  }
}

function deleteFromLocalStorage() {
  // Use the availability check function
  if (!isLocalStorageAvailable()) {
    // console.warn("Attempted to delete from LocalStorage, but it's unavailable.");
    return; // Can't delete if not available
  }
  try {
    window.localStorage.removeItem(CART_KEY);
    // console.log("Cart deleted from LocalStorage");
  } catch (error) {
    // Catch errors during the removeItem process itself
    console.error("Error during deleteFromLocalStorage operation:", error);
  }
}

// --- Cart Service ---

let memoryCart = null; // In-memory cache

const CartStorage = {
  initializeCart: async () => {
    if (memoryCart) return memoryCart;

    let cart = null;
    let migrationNeeded = false;
    let legacyKeyUsed = false;

    // 1. Check IndexedDB (New Key)
    try {
      cart = await getFromIndexedDB(); // Uses CART_KEY = "cart"
      if (cart) {
        memoryCart = cart;
        // console.log("Initialized cart from IndexedDB (New Key)");
        return memoryCart;
      }
    } catch (error) {
      console.warn(`Failed to get cart from IndexedDB: ${error.message}`);
    }

    // 2. Check LocalStorage (New Key - as fallback if IDB failed before)
    if (!cart && isLocalStorageAvailable()) {
      try {
        cart = getFromLocalStorage(); // Uses CART_KEY = "cart"
        if (cart) {
          memoryCart = cart;
          // console.log("Initialized cart from LocalStorage (New Key - Fallback)");
          migrationNeeded = true; // Needs migration from LS (new key) to IDB
          // Don't return yet, proceed to check legacy key just in case,
          // but prioritize this one if legacy is also somehow present.
        }
      } catch (error) {
        console.warn(
          `Error during LocalStorage (New Key) initialization attempt: ${error.message}`,
        );
      }
    }

    // 3. Check LocalStorage (Legacy Key - for old users)
    let legacyCart = null;
    if (isLocalStorageAvailable()) {
      // Check LS availability again
      try {
        const legacyCartString = window.localStorage.getItem(LEGACY_CART_KEY);
        if (legacyCartString) {
          legacyCart = JSON.parse(legacyCartString);
          // console.log("Found cart data using Legacy Key");
          if (!cart) {
            // Only use legacy cart if no cart found under the new key
            cart = legacyCart;
            memoryCart = cart;
            migrationNeeded = true; // Needs migration from LS (legacy key) to IDB
            legacyKeyUsed = true;
            // console.log("Using cart from Legacy Key");
          }
        }
      } catch (error) {
        console.warn(
          `Error checking/parsing legacy cart key (${LEGACY_CART_KEY}): ${error.message}`,
        );
        // Attempt to clear potentially corrupted legacy item
        try {
          window.localStorage.removeItem(LEGACY_CART_KEY);
        } catch (e) {}
      }
    }

    // 4. Perform Migration if needed (either from LS fallback or legacy key)
    if (cart && migrationNeeded) {
      // console.log(`Attempting migration to IndexedDB for cart found via ${legacyKeyUsed ? 'Legacy Key' : 'New Key Fallback'}...`);
      try {
        await saveToIndexedDB(cart); // Save the found cart to IDB (uses new key "cart")
        // console.log("Migration to IndexedDB successful.");
        // If migration succeeded, clean up the source
        if (legacyKeyUsed && isLocalStorageAvailable()) {
          // console.log(`Removing legacy cart item (${LEGACY_CART_KEY}) after successful migration.`);
          try {
            window.localStorage.removeItem(LEGACY_CART_KEY);
          } catch (e) {
            console.warn(`Failed to remove legacy cart key after migration: ${e.message}`);
          }
        } else if (!legacyKeyUsed && isLocalStorageAvailable()) {
          // If we migrated from the new key fallback, remove it from LS
          // console.log(`Removing LS fallback item (${CART_KEY}) after successful migration.`);
          try {
            window.localStorage.removeItem(CART_KEY);
          } catch (e) {
            console.warn(`Failed to remove LS fallback key after migration: ${e.message}`);
          }
        }
      } catch (migrationError) {
        console.warn(`Failed to migrate cart to IndexedDB: ${migrationError.message}`);
        // If migration fails, the data remains in memoryCart and potentially in LS source
      }
    }

    // 5. Set to initialCart if nothing found
    if (!cart) {
      // console.log("Initializing with empty cart (no persistent storage found).");
      memoryCart = initialCart;
    }

    // Make sure memoryCart is set if cart was found but migration logic didn't run/set it
    if (cart && !memoryCart) {
      memoryCart = cart;
    }

    return memoryCart;
  },

  // Save cart state to memory and persistent storage
  saveCart: async (cartData) => {
    if (!cartData) return;
    memoryCart = { ...cartData }; // Update in-memory cache first

    try {
      await saveToIndexedDB(memoryCart); // Uses new key "cart"
      // If IndexedDB succeeds, try to remove from LocalStorage (if available)
      // This targets the fallback key ("cart"), not the legacy one.
      if (isLocalStorageAvailable()) {
        deleteFromLocalStorage(); // Uses new key "cart"
      }
      // console.log("Saved cart to IndexedDB (and attempted LS fallback cleanup)");
    } catch (error) {
      console.warn(
        `Failed to save cart to IndexedDB, falling back to LocalStorage (if available): ${error.message}`,
      );
      // Try saving to LocalStorage only if it's available
      if (isLocalStorageAvailable()) {
        const localStorageSuccess = saveToLocalStorage(memoryCart); // Uses new key "cart"
        if (!localStorageSuccess) {
          console.error(
            "IndexedDB failed AND LocalStorage failed to save. Cart state might be lost on refresh.",
          );
        } else {
          // LS succeeded after IDB fail, try to ensure IDB is clean
          try {
            await deleteFromIndexedDB(); // Uses new key "cart"
          } catch (deleteError) {
            console.warn(
              `Failed to delete potentially inconsistent IndexedDB cart data after LS save: ${deleteError.message}`,
            );
          }
        }
      } else {
        // IndexedDB failed AND LocalStorage is unavailable
        console.error(
          "IndexedDB failed and LocalStorage is unavailable. Cart state might be lost on refresh.",
        );
      }
    }
  },

  // Get the current cart state (from memory)
  getCart: () => {
    return memoryCart || initialCart; // Return memory cache or default
  },

  // Clear cart from memory and persistent storage
  clearCart: async () => {
    memoryCart = initialCart; // Clear memory cache
    try {
      await deleteFromIndexedDB(); // Uses new key "cart"
    } catch (error) {
      console.warn(`Failed to delete cart from IndexedDB during clear: ${error.message}`);
    }
    // Try clearing LocalStorage as well (if available)
    if (isLocalStorageAvailable()) {
      deleteFromLocalStorage(); // Uses new key "cart"
      // Also attempt to clear the legacy key, just in case
      try {
        window.localStorage.removeItem(LEGACY_CART_KEY);
      } catch (e) {}
    }
    // console.log("Cleared cart from memory and attempted persistent storage clear.");
  },

  // Utility to get initial cart structure
  getInitialCart: () => {
    return initialCart;
  },
};

export default CartStorage;
